import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, Info, ChevronLeft, Lock, AlertTriangle } from 'lucide-react';
import i18n from '../../i18n';
import { saveAgentConfig, fetchAgentConfig } from '../../services/aiConfigService';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth'; // Importando useAuth
import UpgradeButton from '../../components/UpgradeButton/UpgradeButton'; // Importando UpgradeButton

const modelOptions = ['Gemini 2.5 Flash'];
const timezoneOptions = [
  // GMT-8 a GMT-3 (Américas)
  'America/Los_Angeles (GMT-8)',
  'America/Phoenix (GMT-7)',
  'America/Mexico_City (GMT-6)',
  'America/Costa_Rica (GMT-6)',
  'America/Bogota (GMT-5)',
  'America/Lima (GMT-5)',
  'America/Panama (GMT-5)',
  'America/Guayaquil (GMT-5)',
  'America/Havana (GMT-5)',
  'America/New_York (GMT-5)',
  'America/Caracas (GMT-4)',
  'America/La_Paz (GMT-4)',
  'America/Santo_Domingo (GMT-4)',
  'America/Puerto_Rico (GMT-4)',
  'America/Santiago (GMT-4)',
  'America/Asuncion (GMT-4)',
  'America/Sao_Paulo (GMT-3)',
  'America/Argentina/Buenos_Aires (GMT-3)',
  'America/Montevideo (GMT-3)',

  // Europa
  'Europe/Lisbon (GMT+0)',
  'Europe/London (GMT+0)',
  'Europe/Madrid (GMT+1)',
  'Europe/Paris (GMT+1)',

  // Ásia e Oceania
  'Asia/Tokyo (GMT+9)',
  'Australia/Sydney (GMT+10)',
];

const WhatsappAgentConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useAuth();

  const isProUser = profile?.subscription_status === 'pro';

  const [activeTab, setActiveTab] = useState<'model' | 'prompt'>('model');
  const [isLoading, setIsLoading] = useState(true);

  // Model Config States
  const [temperature, setTemperature,] = useState(0.5);
  const [selectedModel, setSelectedModel] = useState(modelOptions[0]);
  const [knowledgeRestriction, setKnowledgeRestriction] = useState(false);
  const [languageDetector, setLanguageDetector] = useState(true);
  const [timezoneSetting, setTimezoneSetting] = useState(t('ai.config.timezone_include'));
  const [selectedTimezone, setSelectedTimezone] = useState(timezoneOptions[0]);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  // Prompt Config States
  const [agentName, setAgentName] = useState('');
  const [agentFunction, setAgentFunction] = useState('');
  const [agentObjective, setAgentObjective] = useState('');
  const [agentResponse, setAgentResponse] = useState('');
  const [agentInstructions, setAgentInstructions] = useState('');
  const [agentAvoidThemes, setAgentAvoidThemes] = useState('');
  const [agentAvoidWords, setAgentAvoidWords] = useState('');
  const [agentCustomRules, setAgentCustomRules] = useState('');

  // Full Prompt State (Mantido apenas para carregar/salvar, mas não exibido)
  const [fullPrompt, setFullPrompt] = useState('');

  // Agent Activation States
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [isTogglingAgent, setIsTogglingAgent] = useState(false);

  const availableLanguages = [
    { code: 'pt-BR', name: t('languages.pt-BR') },
    { code: 'es', name: t('languages.es') },
    { code: 'en', name: t('languages.en') },
    { code: 'fr', name: t('languages.fr') },
  ];

  // --- Carregamento Inicial ---
  useEffect(() => {
    if (!isProUser) {
      setIsLoading(false);
      return;
    }

    const loadConfig = async () => {
      try {
        const config = await fetchAgentConfig('whatsapp_agent');
        if (config) {
          // Carregar Model Config
          if (config.model_config) {
            setTemperature(config.model_config.temperature ?? 0.5);
            setSelectedModel(config.model_config.selectedModel ?? modelOptions[0]);
            setKnowledgeRestriction(config.model_config.knowledgeRestriction ?? false);
            setLanguageDetector(config.model_config.languageDetector ?? true);
            setTimezoneSetting(config.model_config.timezoneSetting ?? t('ai.config.timezone_include'));
            setSelectedTimezone(config.model_config.selectedTimezone ?? timezoneOptions[0]);
            setSelectedLanguage(config.model_config.selectedLanguage ?? i18n.language);
          }
          // Carregar Prompt Config
          if (config.prompt_config) {
            setAgentName(config.prompt_config.agentName ?? '');
            setAgentFunction(config.prompt_config.agentFunction ?? '');
            setAgentObjective(config.prompt_config.agentObjective ?? '');
            setAgentResponse(config.prompt_config.agentResponse ?? '');
            setAgentInstructions(config.prompt_config.agentInstructions ?? '');
            setAgentAvoidThemes(config.prompt_config.agentAvoidThemes ?? '');
            setAgentAvoidWords(config.prompt_config.agentAvoidWords ?? '');
            setAgentCustomRules(config.prompt_config.agentCustomRules ?? '');
          }
          // Carregar Full Prompt
          setFullPrompt(config.full_prompt ?? '');
          // Carregar Agent Activation Status
          setIsAgentActive((config as any).is_active ?? false);
        }
      } catch (error) {
        toast.error(t('Falha ao carregar configurações do agente.'));
      } finally {
        setIsLoading(false);
      }
    };
    loadConfig();
  }, [t, isProUser]);


  const handleSave = async () => {
    if (!isProUser) {
      toast.error(t('Funcionalidade exclusiva para o plano PRO.'));
      return;
    }

    setIsLoading(true);

    const model_config = {
      temperature,
      selectedModel,
      knowledgeRestriction,
      languageDetector,
      timezoneSetting,
      selectedTimezone,
      selectedLanguage,
    };

    const prompt_config = {
      agentName,
      agentFunction,
      agentObjective,
      agentResponse,
      agentInstructions,
      agentAvoidThemes,
      agentAvoidWords,
      agentCustomRules,
    };

    try {
      // Enviamos o full_prompt atual (que pode ser o último gerado pelo n8n)
      await saveAgentConfig({
        agent_type: 'whatsapp_agent',
        model_config,
        prompt_config,
        full_prompt: fullPrompt,
      });
      toast.success(t('Configurações do Agente WhatsApp salvas com sucesso! O prompt completo será gerado em breve.'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao salvar configurações.');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  const handleToggleAgent = async (newStatus: boolean) => {
    if (!isProUser) {
      toast.error(t('Funcionalidade exclusiva para o plano PRO.'));
      return;
    }

    setIsTogglingAgent(true);

    try {
      // Dinamically import AI agent service functions
      const { activateAIAgent, deactivateAIAgent } = await import('../../services/aiAgentService');

      if (newStatus) {
        // Activate agent
        await activateAIAgent();
        setIsAgentActive(true);
        toast.success(t('ai.config.activation_success'));
      } else {
        // Deactivate agent
        await deactivateAIAgent();
        setIsAgentActive(false);
        toast.success(t('ai.config.deactivation_success'));
      }
    } catch (error) {
      console.error('[AI Agent Toggle] Error:', error);
      const errorMessage = error instanceof Error ? error.message : t('ai.config.activation_error');
      toast.error(errorMessage);
    } finally {
      setIsTogglingAgent(false);
    }
  };

  const renderModelConfig = () => (
    <div className="space-y-8">
      {/* Modelo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="model-select">{t('ai.config.model_label')}</label>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="relative">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={!isProUser}
            >
              {modelOptions.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
          <motion.button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!isProUser}
          >
            <Settings className="size-4" />
            <span>{t('ai.config.model_settings_button')}</span>
          </motion.button>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('ai.config.model_description')}</p>
      </div>

      {/* Temperatura */}
      <div>
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="temperature-slider">
          <span>{t('ai.config.temperature_label')}</span>
          <span title={t('ai.config.temperature_tooltip')}>
            <Info className="size-4 text-gray-400" />
          </span>
        </label>
        <div className="mt-4">
          <input
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50"
            id="temperature-slider"
            max="1"
            min="0"
            step="0.1"
            type="range"
            value={temperature}
            onChange={(e) => setTemperature(Number(e.target.value))}
            disabled={!isProUser}
          />
          <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            {Array.from({ length: 11 }, (_, i) => (
              <span key={i}>{i / 10}</span>
            ))}
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('ai.config.temperature_description')}</p>
      </div>

      {/* Comportamento */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('ai.config.behavior_title')}</h3>

        {/* Restrição de conhecimento */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
              id="knowledge-restriction"
              name="knowledge-restriction"
              type="checkbox"
              checked={knowledgeRestriction}
              onChange={() => setKnowledgeRestriction(!knowledgeRestriction)}
              disabled={!isProUser}
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-200" htmlFor="knowledge-restriction">
              <span>{t('ai.config.knowledge_restriction_label')}</span>
              <span title={t('ai.config.knowledge_restriction_tooltip')}>
                <Info className="size-4 text-gray-400" />
              </span>
            </label>
            <p className="text-gray-500 dark:text-gray-400" id="knowledge-restriction-description">{t('ai.config.knowledge_restriction_description')}</p>
          </div>
        </div>

        {/* Detector de Idioma */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
              id="language-detector"
              name="language-detector"
              type="checkbox"
              checked={languageDetector}
              onChange={() => setLanguageDetector(!languageDetector)}
              disabled={!isProUser}
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-200" htmlFor="language-detector">
              <span>{t('ai.config.language_detector_label')}</span>
              <span title={t('ai.config.language_detector_tooltip')}>
                <Info className="size-4 text-gray-400" />
              </span>
            </label>
            <p className="text-gray-500 dark:text-gray-400" id="language-detector-description">{t('ai.config.language_detector_description')}</p>
          </div>
        </div>
      </div>

      {/* Fuso Horário */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="timezone-select">{t('ai.config.timezone_label')}</label>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="relative">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              id="timezone-toggle"
              value={timezoneSetting}
              onChange={(e) => setTimezoneSetting(e.target.value)}
              disabled={!isProUser}
            >
              <option>{t('ai.config.timezone_include')}</option>
              <option>{t('ai.config.timezone_exclude')}</option>
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
          <div className="relative">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              id="timezone-select"
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              disabled={timezoneSetting === t('ai.config.timezone_exclude') || !isProUser}
            >
              {timezoneOptions.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
        </div>
      </div>

      {/* Idioma do Agente */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="agent-language-select">{t('ai.config.agent_language_label')}</label>
        <div className="relative mt-2 max-w-xs">
          <select
            className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            id="agent-language-select"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            disabled={!isProUser}
          >
            {availableLanguages.map(lang => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
        </div>
      </div>
      {/* Agent Activation Toggle */}
      <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          {t('ai.config.activation_title')}
        </h3>

        {/* Toggle Switch */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('ai.config.activation_label')}
            </span>
            <span className={`text-sm font-semibold ${isAgentActive ? 'text-green-600 dark:text-green-500' : 'text-gray-500 dark:text-gray-400'}`}>
              {isAgentActive ? t('ai.config.activation_active') : t('ai.config.activation_inactive')}
            </span>
          </div>

          <button
            onClick={() => handleToggleAgent(!isAgentActive)}
            disabled={isTogglingAgent || !isProUser}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${isAgentActive ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAgentActive ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>

        {/* Disclaimer */}
        <motion.div
          className="mt-4 flex gap-3 items-start p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-amber-900 dark:text-amber-100 mb-1">
              {t('ai.config.activation_disclaimer_title')}
            </h4>
            <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
              {t('ai.config.activation_disclaimer_message')}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );

  const renderPromptConfig = () => (
    <div className="space-y-8" id="prompt-settings">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">

        {/* Quem é o seu Agente? */}
        <div>
          <label className="block text-lg font-bold text-[#181411] dark:text-white" htmlFor="agent-name">{t('ai.prompt.agent_name_title')}</label>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('ai.prompt.agent_name_description')}</p>
          <textarea
            className="mt-4 w-full rounded-lg border-gray-300 bg-gray-50 dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition p-4 disabled:opacity-50 disabled:cursor-not-allowed"
            id="agent-name"
            rows={4}
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            disabled={!isProUser}
          ></textarea>
        </div>

        {/* O que seu Agente faz? */}
        <div>
          <label className="block text-lg font-bold text-[#181411] dark:text-white" htmlFor="agent-function">{t('ai.prompt.agent_function_title')}</label>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('ai.prompt.agent_function_description')}</p>
          <textarea
            className="mt-4 w-full rounded-lg border-gray-300 bg-gray-50 dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition p-4 disabled:opacity-50 disabled:cursor-not-allowed"
            id="agent-function"
            rows={4}
            value={agentFunction}
            onChange={(e) => setAgentFunction(e.target.value)}
            disabled={!isProUser}
          ></textarea>
        </div>

        {/* Qual o objetivo do seu Agente? */}
        <div>
          <label className="block text-lg font-bold text-[#181411] dark:text-white" htmlFor="agent-objective">{t('ai.prompt.agent_objective_title')}</label>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('ai.prompt.agent_objective_description')}</p>
          <textarea
            className="mt-4 w-full rounded-lg border-gray-300 bg-gray-50 dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition p-4 disabled:opacity-50 disabled:cursor-not-allowed"
            id="agent-objective"
            rows={4}
            value={agentObjective}
            onChange={(e) => setAgentObjective(e.target.value)}
            disabled={!isProUser}
          ></textarea>
        </div>

        {/* Como seu Agente deve responder? */}
        <div>
          <label className="block text-lg font-bold text-[#181411] dark:text-white" htmlFor="agent-response">{t('ai.prompt.agent_response_title')}</label>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('ai.prompt.agent_response_description')}</p>
          <textarea
            className="mt-4 w-full rounded-lg border-gray-300 bg-gray-50 dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition p-4 disabled:opacity-50 disabled:cursor-not-allowed"
            id="agent-response"
            rows={4}
            value={agentResponse}
            onChange={(e) => setAgentResponse(e.target.value)}
            disabled={!isProUser}
          ></textarea>
        </div>
      </div>

      {/* Regras Gerais Section */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-6 dark:border-gray-700/50 dark:bg-gray-900/20">
        <h3 className="text-xl font-bold text-[#181411] dark:text-white">{t('ai.prompt.general_rules_title')}</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('ai.prompt.general_rules_description')}</p>

        <div className="mt-6 space-y-6">

          {/* Instruções para o Agente */}
          <div>
            <label className="block text-base font-semibold text-[#181411] dark:text-gray-200" htmlFor="agent-instructions">{t('ai.prompt.instructions_title')}</label>
            <input
              className="mt-2 w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              id="agent-instructions"
              placeholder={t('ai.prompt.instructions_placeholder')}
              type="text"
              value={agentInstructions}
              onChange={(e) => setAgentInstructions(e.target.value)}
              disabled={!isProUser}
            />
          </div>

          {/* Quais temas ele deve evitar? */}
          <div>
            <label className="block text-base font-semibold text-[#181411] dark:text-gray-200" htmlFor="agent-avoid-themes">{t('ai.prompt.avoid_themes_title')}</label>
            <input
              className="mt-2 w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              id="agent-avoid-themes"
              placeholder={t('ai.prompt.avoid_themes_placeholder')}
              type="text"
              value={agentAvoidThemes}
              onChange={(e) => setAgentAvoidThemes(e.target.value)}
              disabled={!isProUser}
            />
          </div>

          {/* Quais palavras ele deve evitar? */}
          <div>
            <label className="block text-base font-semibold text-[#181411] dark:text-gray-200" htmlFor="agent-avoid-words">{t('ai.prompt.avoid_words_title')}</label>
            <input
              className="mt-2 w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              id="agent-avoid-words"
              placeholder={t('ai.prompt.avoid_words_placeholder')}
              type="text"
              value={agentAvoidWords}
              onChange={(e) => setAgentAvoidWords(e.target.value)}
              disabled={!isProUser}
            />
          </div>

          {/* Regras personalizadas */}
          <div>
            <label className="block text-base font-semibold text-[#181411] dark:text-gray-200" htmlFor="agent-custom-rules">{t('ai.prompt.custom_rules_title')}</label>
            <textarea
              className="mt-2 w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 transition p-4 disabled:opacity-50 disabled:cursor-not-allowed"
              id="agent-custom-rules"
              placeholder={t('ai.prompt.custom_rules_placeholder')}
              rows={3}
              value={agentCustomRules}
              onChange={(e) => setAgentCustomRules(e.target.value)}
              disabled={!isProUser}
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isProUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-6 lg:p-10 max-w-4xl mx-auto flex flex-col gap-8 text-center"
      >
        <div className="mb-8">
          <motion.h1
            className="text-4xl font-black leading-tight tracking-[-0.033em] text-red-500 dark:text-red-400 flex items-center justify-center gap-3"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <Lock className="size-8" /> {t('ai.config.whatsapp_agent_title')}
          </motion.h1>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
            {t('Funcionalidade exclusiva para o plano PRO.')}
          </p>
          <p className="mt-2 text-gray-500 dark:text-gray-500">
            {t('Faça upgrade para desbloquear o Agente de Atendimento WhatsApp e automatizar suas respostas.')}
          </p>
        </div>
        <div className="w-full max-w-xs mx-auto">
          <UpgradeButton text={t('Fazer Upgrade para PRO')} />
        </div>
        <Link to="/ia" className="mt-4">
          <motion.button
            className="flex items-center mx-auto gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft className="size-5" />
            {t('ai.config.back_to_ai')}
          </motion.button>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 lg:p-10 max-w-7xl mx-auto"
    >
      <div className="mx-auto max-w-4xl">

        {/* Header e Botão Voltar */}
        <div className="mb-8 flex items-center gap-4">
          <Link to="/ia">
            <motion.button
              className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="size-5" />
              {t('ai.config.back_to_ai')}
            </motion.button>
          </Link>
        </div>

        <div className="mb-8">
          <p className="text-[#181411] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]">{t('ai.config.whatsapp_agent_title')}</p>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">{t('ai.config.whatsapp_agent_subtitle')}</p>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/50 p-6 sm:p-8 card">

          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200 dark:border-gray-700/50">
            <div aria-label="Tabs" className="flex -mb-px">
              <motion.button
                onClick={() => setActiveTab('model')}
                className={`relative whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'model'
                  ? 'text-primary border-primary'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  } mr-8`}
                whileHover={{ y: activeTab === 'model' ? 0 : -2 }}
              >
                {t('ai.config.tab_model')}
                {activeTab === 'model' && (
                  <motion.span
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-orange-400 to-primary"
                    layoutId="ai-tab-underline-whatsapp"
                  />
                )}
              </motion.button>

              <motion.button
                onClick={() => setActiveTab('prompt')}
                className={`relative whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'prompt'
                  ? 'text-primary border-primary'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                whileHover={{ y: activeTab === 'prompt' ? 0 : -2 }}
              >
                {t('ai.config.tab_prompt')}
                {activeTab === 'prompt' && (
                  <motion.span
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-orange-400 to-primary"
                    layoutId="ai-tab-underline-whatsapp"
                  />
                )}
              </motion.button>
            </div>
          </div>

          {/* Conteúdo das Abas */}
          <div className="min-h-64">
            {activeTab === 'model' && renderModelConfig()}
            {activeTab === 'prompt' && renderPromptConfig()}
          </div>

          {/* Botão Salvar */}
          <div className="mt-8 flex justify-end">
            <motion.button
              onClick={handleSave}
              disabled={isLoading || !isProUser}
              className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-br from-orange-400 to-primary px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:from-orange-500 hover:to-orange-600 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isLoading || !isProUser ? 1 : 1.05 }}
              whileTap={{ scale: isLoading || !isProUser ? 1 : 0.95 }}
            >
              <span className="material-symbols-outlined text-lg">save</span>
              <span>{t('sidebar.save_changes')}</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WhatsappAgentConfigPage;